import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';
import EmptyPage from '../EmptyPage/EmptyPage';
import StudyCard from './StudyCard';
import StudyCardSkeleton from './StudyCardSkeleton';
import StudyFilters from './StudyFilters';
import UploadStudyModal from './UploadStudyModal';

import { Fab } from '@mui/material';
import Grid from '@mui/material/Grid';

import wrapPage from 'components/atoms/wrapPage/wrapPage';
import AdvancedSearchDrawer from 'components/SearchFilters/AdvancedSearch';
import { SITE_HEADER_NUMBER } from 'components/SiteHeader/SiteHeader';
import { CancerType } from 'interfaces/cancerType';
import { Permission } from 'interfaces/permissionOption';
import { find, isEmpty, map, values } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import { isPfizerDemoLab } from 'utils/demo';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';

import { useAllCancerTypes } from 'utils/queryHooks/uiConstantsHooks';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useLabUploadSummary } from 'utils/queryHooks/useLabUploadSummary';
import { useStudies } from 'utils/queryHooks/useStudies';
import { FilteredStudyResult, filterStudies } from './filterStudies';
import { LabExternalStorageSettingsControl } from './LabExternalStorageSettings/LabExternalStorageSettingsControl';
import LabUploadSummaryRow from './LabUploadSummaryRow';
import { useStudyFiltersForm } from './useStudyFiltersForm';

export const SELF_SERVICE = 'self service';
export const PROJECT = 'project';

export const StudiesDashboard: React.FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const theme = useTheme();
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const { labId } = useCurrentLabId();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: studiesLoading, data: allStudies } = useStudies(labId, true, { queriedEntityName: 'studies' });
  const {
    data: uploadSummary,
    isLoading: isUploadSummaryLoading,
    isError: isUploadSummaryError,
  } = useLabUploadSummary(labId);
  const { data: availableLabs = [], isLoading: isLoadingLabs } = useLabs();
  const { allCancerTypes } = useAllCancerTypes();

  const { all: labs } = useAppSelector((state) => (state as any).labs);
  const cancers: CancerType[] = values(allCancerTypes);
  const currentLab = find(availableLabs, { id: labId });
  const currentLabName = currentLab?.name || find(labs, { id: labId })?.name;

  const formMethods = useStudyFiltersForm();

  const { text: textFormValue, startDate, endDate } = formMethods.watch();
  const filteredStudies = filterStudies(allStudies, { text: textFormValue, startDate, endDate });

  const { hasPermission } = usePermissions();
  const canApproveCases = hasPermission(Permission.ApproveCases);

  const pfizerDemoLab = isPfizerDemoLab(labId);

  const [isUploadStudyModalOpen, setIsUploadStudyModalOpen] = useState(false);
  const [hasStickyHeaderShadow, setHasStickyHeaderShadow] = useState(false);
  const shouldShowFilters = studiesLoading || !isEmpty(allStudies);

  useEffect(() => {
    const shouldOpenCreateStudy = Boolean(
      (location.state as { openCreateStudyModal?: boolean } | null)?.openCreateStudyModal
    );
    if (!shouldOpenCreateStudy) {
      return;
    }

    setIsUploadStudyModalOpen(true);
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const stickyHeader = stickyHeaderRef.current;

    if (!stickyHeader) {
      return;
    }

    const findScrollParent = (element: HTMLElement) => {
      let current: HTMLElement | null = element.parentElement;

      while (current) {
        const { overflowY } = window.getComputedStyle(current);

        if (/(auto|scroll|overlay)/.test(overflowY) && current.scrollHeight > current.clientHeight) {
          return current;
        }

        current = current.parentElement;
      }

      return window;
    };

    const scrollParent = findScrollParent(stickyHeader);
    const updateShadow = () => {
      const scrollTop = scrollParent instanceof Window ? scrollParent.scrollY : scrollParent.scrollTop;
      setHasStickyHeaderShadow(scrollTop > 8);
    };

    updateShadow();
    scrollParent.addEventListener('scroll', updateShadow, { passive: true });

    return () => {
      scrollParent.removeEventListener('scroll', updateShadow);
    };
  }, []);

  const renderStudySkeletons = () =>
    map(Array.from({ length: 6 }), (_, index) => <StudyCardSkeleton key={`study-skeleton-${index}`} />);

  const renderStudies = () => {
    if (studiesLoading && isEmpty(allStudies)) {
      return renderStudySkeletons();
    } else if (isEmpty(allStudies)) {
      return <EmptyPage page="studies" />;
    } else if (isEmpty(filteredStudies)) {
      return (
        <Box
          sx={{
            px: 4,
            py: 8,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6">No studies matched these filters</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {textFormValue?.trim()
              ? `No studies matched "${textFormValue.trim()}". Try a broader term or clear some filters.`
              : 'Adjust the created-date filters or clear them to see more studies.'}
          </Typography>
        </Box>
      );
    } else {
      return map(filteredStudies, ({ study, matchedFields }: FilteredStudyResult) => (
        <StudyCard
          key={study.id}
          study={study}
          matchedFields={matchedFields}
          searchQuery={textFormValue}
          lastUploadedAt={uploadSummary?.byStudy[study.id]?.lastUploadedAt}
          isUploadSummaryLoading={isUploadSummaryLoading && !uploadSummary}
          isUploadSummaryUnavailable={isUploadSummaryError && !uploadSummary}
        />
      ));
    }
  };

  return (
    <>
      {isUploadStudyModalOpen && (
        <UploadStudyModal
          isAdmin={canApproveCases}
          cancerTypes={cancers}
          onClose={() => setIsUploadStudyModalOpen(false)}
        />
      )}
      <Box
        ref={stickyHeaderRef}
        sx={{
          position: 'sticky',
          top: SITE_HEADER_NUMBER,
          zIndex: theme.zIndex.appBar - 1,
          mb: 3,
          width: '100vw',
          ml: 'calc(50% - 50vw)',
          mr: 'calc(50% - 50vw)',
          backgroundColor: 'transparent',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          boxShadow: hasStickyHeaderShadow
            ? `0 8px 16px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.16 : 0.045)}`
            : 'none',
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Box
          sx={{
            maxWidth: `${theme.breakpoints.values.lg}px`,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            pt: 3,
            pb: 2,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Grid item>
              {isLoadingLabs && !currentLabName ? (
                <Skeleton variant="rectangular" width={320} height={40} />
              ) : (
                <Typography variant="h1">{currentLabName ? `${currentLabName} Studies` : 'Studies'}</Typography>
              )}
            </Grid>
            <Grid item>
              <Grid container spacing={1} alignItems="center">
                <Grid item>
                  <LabExternalStorageSettingsControl
                    labId={labId}
                    labName={currentLabName}
                    currentLabStudyDefaultSettings={currentLab?.defaultStudySettings}
                  />
                </Grid>
                {!pfizerDemoLab && (
                  <Grid item>
                    <Tooltip title="Create New Study" placement="right-start">
                      <Fab color="primary" onClick={() => setIsUploadStudyModalOpen(true)}>
                        <AddIcon fontSize="large" />
                      </Fab>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Box sx={{ mb: shouldShowFilters ? 3 : 1 }}>
            <LabUploadSummaryRow
              labId={labId}
              summary={uploadSummary}
              isLoading={isUploadSummaryLoading && !uploadSummary}
              isUnavailable={isUploadSummaryError && !uploadSummary}
            />
          </Box>
          {shouldShowFilters && (
            <Grid container alignItems="center">
              <StudyFilters formMethods={formMethods} />
            </Grid>
          )}
        </Box>
      </Box>

      <AdvancedSearchDrawer showApprovedFilter={canApproveCases} targetUrl="/procedures" />
      <div>{renderStudies()}</div>
    </>
  );
};

export default wrapPage(StudiesDashboard);
